import React, { useState } from 'react';
import {
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  CircularProgress,
  Container,
  Grid,
  Card,
  CardContent,
  Snackbar
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import {
  useMountUsageCheck,
  handleGenerateUsage,
  trackGenerationStarted,
  trackGenerationCompleted,
  trackGenerationFailed,
  trackResultCopied,
} from '../lib/usageGate';

const TOOL_ID = 'aba_assessment_template_generator';

const ABAAssessmentTemplateGenerator = () => {
  const [clientInfo, setClientInfo] = useState('');
  const [assessmentType, setAssessmentType] = useState('');
  const [generatedTemplate, setGeneratedTemplate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  useMountUsageCheck(TOOL_ID);

  const generateTemplate = async () => {
    setLoading(true);
    setError(null);
    const t0 = Date.now();
    trackGenerationStarted(TOOL_ID, { assessment_type: assessmentType });
    try {
      const response = await fetch('/api/generateABAAssessmentTemplate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          clientInfo,
          assessmentType,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate assessment template');
      }
      const data = await response.json();
      setGeneratedTemplate(data.template);
      trackGenerationCompleted(TOOL_ID, { duration_ms: Date.now() - t0 });
      handleGenerateUsage(TOOL_ID, data.usage);
    } catch (error) {
      console.error('Error generating assessment template:', error);
      setError('Failed to generate assessment template. Please try again.');
      trackGenerationFailed(TOOL_ID, { error_code: error.message });
    }
    setLoading(false);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedTemplate).then(() => {
      setSnackbarOpen(true);
      trackResultCopied(TOOL_ID);
    });
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 3, backgroundColor: 'background.default' }}>
        <Typography variant="h4" gutterBottom align="center" sx={{ color: 'primary.main', mb: 4 }}>
          ABA Assessment Template Generator for Insurance
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Client Information"
              value={clientInfo}
              onChange={(e) => setClientInfo(e.target.value)}
              placeholder="Enter relevant client information (e.g., age, diagnosis, current skills, areas of concern)"
              sx={{ backgroundColor: 'white' }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Assessment Type"
              value={assessmentType}
              onChange={(e) => setAssessmentType(e.target.value)}
              placeholder="e.g., Initial Assessment, FBA, VB-MAPP, ABLLS-R"
              sx={{ backgroundColor: 'white' }}
            />
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={generateTemplate}
              disabled={loading || !clientInfo || !assessmentType}
              fullWidth
            >
              Generate Assessment Template
            </Button>
          </Grid>
        </Grid>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}><CircularProgress /></Box>}
        {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
        {generatedTemplate && (
          <Card elevation={2} sx={{ mt: 4, backgroundColor: 'white' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Generated ABA Assessment Template:</Typography>
              <ReactMarkdown>{generatedTemplate}</ReactMarkdown>
              <Button
                variant="contained"
                color="secondary"
                onClick={copyToClipboard}
                sx={{ mt: 2 }}
              >
                Copy to Clipboard
              </Button>
            </CardContent>
          </Card>
        )}
      </Paper>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message="Template copied to clipboard"
      />
    </Container>
  );
};

export default ABAAssessmentTemplateGenerator;